import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AutoComplete, Input } from 'antd';
import { GoogleNamespace, loadGoogleMaps } from '@app/utils/googleMaps';

/**
 * Ô nhập địa chỉ có gợi ý của Google Places.
 *
 * Chọn từ gợi ý sẽ kèm theo định danh địa điểm + toạ độ, nhờ đó backend tính km và dựng link
 * Google Maps trỏ đúng điểm thay vì để Google đoán lại chuỗi địa chỉ mỗi lần gọi.
 * Chưa cấu hình khoá (hoặc Google lỗi) thì rơi về ô nhập thường — không chặn việc lưu đơn.
 */
export interface PlaceSelection {
  address: string;
  place_id: string;
  lat: number;
  lng: number;
  formatted_address: string;
}

interface AddressAutocompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (place: PlaceSelection) => void;
  /** Gõ tay (không chọn gợi ý) → toạ độ cũ không còn đúng địa chỉ nữa. */
  onManualInput?: () => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

const DEBOUNCE_MS = 300;

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  onManualInput,
  placeholder,
  rows = 4,
  disabled,
}) => {
  const [options, setOptions] = useState<{ value: string; label: string; placeId: string }[]>([]);
  const google = useRef<GoogleNamespace | null>(null);
  const sessionToken = useRef<any>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let ignore = false;
    loadGoogleMaps().then((namespace) => {
      if (!ignore) google.current = namespace;
    });
    return () => {
      ignore = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // Một phiên gõ → chọn chỉ tính một lần cước, nên tạo token một lần rồi bỏ sau khi chọn.
  const currentSession = () => {
    const places = google.current?.maps?.places;
    if (!places) return null;
    if (!sessionToken.current) sessionToken.current = new places.AutocompleteSessionToken();
    return sessionToken.current;
  };

  const fetchPredictions = useCallback((input: string) => {
    const places = google.current?.maps?.places;
    if (!places || input.trim().length < 3) {
      setOptions([]);
      return;
    }

    new places.AutocompleteService().getPlacePredictions(
      {
        input,
        sessionToken: currentSession(),
        componentRestrictions: { country: 'vn' },
        language: 'vi',
      },
      (predictions: any[] | null) => {
        setOptions(
          (predictions ?? []).map((prediction) => ({
            value: prediction.description,
            label: prediction.description,
            placeId: prediction.place_id,
          })),
        );
      },
    );
  }, []);

  const handleChange = (next: string) => {
    onChange?.(next);
    onManualInput?.();
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchPredictions(next), DEBOUNCE_MS);
  };

  const handleSelect = (selected: string, option: { placeId: string }) => {
    const places = google.current?.maps?.places;
    onChange?.(selected);
    setOptions([]);
    if (!places || !onSelect) return;

    new places.PlacesService(document.createElement('div')).getDetails(
      {
        placeId: option.placeId,
        fields: ['place_id', 'geometry', 'formatted_address'],
        sessionToken: currentSession(),
      },
      (place: any, status: string) => {
        // Token chỉ dùng cho một phiên gõ → chọn; huỷ để phiên sau tính riêng.
        sessionToken.current = null;
        if (status !== places.PlacesServiceStatus.OK || !place?.geometry) return;

        onSelect({
          address: selected,
          place_id: place.place_id,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          formatted_address: place.formatted_address ?? selected,
        });
      },
    );
  };

  return (
    <AutoComplete
      value={value}
      options={options}
      onChange={handleChange}
      onSelect={handleSelect as any}
      disabled={disabled}
      style={{ width: '100%' }}
    >
      {/* autoSize: AntD tự đặt height inline theo số dòng — không bị CSS ô search của
          Select (height/line-height cố định) bóp lại; giãn theo địa chỉ dài tới 6 dòng. */}
      <Input.TextArea
        placeholder={placeholder}
        autoSize={{ minRows: rows, maxRows: Math.max(rows, 6) }}
        style={{ borderRadius: 8 }}
      />
    </AutoComplete>
  );
};

export default AddressAutocomplete;
